import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Work,
  People,
  CurrencyRupee,
  Visibility,
  BookmarkBorder,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../../apiconfig";
import "../dashboard/dashboard.css";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: [],
    recentJobs: [],
    recentApplications: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const isSmallMobile = useMediaQuery("(max-width: 375px)");
  const emptyApplicationsIllustration =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='220' viewBox='0 0 320 220'%3E%3Crect width='320' height='220' fill='%23F1F5F9'/%3E%3Ccircle cx='62' cy='56' r='26' fill='%23DBEAFE'/%3E%3Crect x='98' y='30' width='172' height='18' rx='9' fill='%23E5E7EB'/%3E%3Crect x='98' y='56' width='138' height='12' rx='6' fill='%23E5E7EB'/%3E%3Crect x='36' y='92' width='248' height='108' rx='14' fill='white' stroke='%23E5E7EB'/%3E%3Crect x='58' y='114' width='56' height='56' rx='10' fill='%23FDE68A'/%3E%3Crect x='130' y='116' width='130' height='12' rx='6' fill='%23CBD5E1'/%3E%3Crect x='130' y='138' width='104' height='10' rx='5' fill='%23E2E8F0'/%3E%3Crect x='130' y='158' width='82' height='10' rx='5' fill='%23E2E8F0'/%3E%3Crect x='216' y='178' width='44' height='8' rx='4' fill='%23FCA5A5'/%3E%3C/svg%3E";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiEndpoints.dashboard, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();

        if (data.status) {
          const bookingStats = data.data?.booking_stats || {};
          const recentJobs = data.data?.recent_jobs || [];
          const activeUsersCount = Number(
            data.data?.user_stats?.pending_users ??
              bookingStats.by_status?.Pending ??
              bookingStats.by_status?.pending ??
              0
          );

          const transformedData = {
            stats: [
              {
                title: "Total Jobs Posted",
                value: data.data?.job_stats?.active_jobs?.toString() || "0",
                change: "+0%",
                icon: <Work />,
                color: "#36656B",
                bgColor: "rgba(54,101,107,0.12)",
              },
              {
                title: "Total Applications",
                value: bookingStats.total_bookings?.toString() || "0",
                change: "+0%",
                icon: <People />,
                color: "#75B06F",
                bgColor: "rgba(117,176,111,0.15)",
              },
              {
                title: "Total Spent",
                value: `\u20B9${(bookingStats.total_spent || 0).toLocaleString()}`,
                change: "+0%",
                icon: <CurrencyRupee />,
                color: "#36656B",
                bgColor: "rgba(54,101,107,0.12)",
              },
              {
                title: "Active Users",
                value: activeUsersCount.toString(),
                change: "+0%",
                icon: <Visibility />,
                color: "#36656B",
                bgColor: "rgba(54,101,107,0.12)",
              },
            ],
            recentJobs: recentJobs.map((job) => ({
              id: job.id || Math.random().toString(36).substr(2, 9),
              title: job.job_title || "Untitled Job",
              status: job.status || "Unknown",
              Description: job.job_description || "No description provided",
              projectDuration: job.project_duration || "Not specified",
              applications: job.application_count || 0,
              budget: job.budget ? `\u20B9${job.budget}` : "\u20B90",
              postedDate: job.time_ago || "Recently",
              progress:
                job.status === "Active"
                  ? Math.min(100, Math.max(0, (job.application_count || 0) * 5))
                  : 0,
            })),
            recentApplications: (data.data?.recent_applications || []).map((app) => ({
              id: app.id || Math.random().toString(36).substr(2, 9),
              name: app.name || "Anonymous",
              domain: app.domain || "Unknown domain",
              profession: app.profession || "Unknown profession",
              job: app.job_title || "Unknown job",
              rating: app.rating || "0",
              experience: app.experience || "0 years",
              appliedDate: app.time_ago || "Recently",
              avatar:
                app.profile_img ||
                "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150",
            })),
          };

          setDashboardData(transformedData);
        } else {
          throw new Error(data.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
        setDashboardData({
          stats: [
            {
              title: "Total Jobs Posted",
              value: "12",
              change: "+2.5%",
              icon: <Work />,
              color: "#36656B",
              bgColor: "rgba(54,101,107,0.12)",
            },
            {
              title: "Total Applications",
              value: "248",
              change: "+12.3%",
              icon: <People />,
              color: "#75B06F",
              bgColor: "rgba(117,176,111,0.15)",
            },
            {
              title: "Total Spent",
              value: "\u20B94,250",
              change: "+15.2%",
              icon: <CurrencyRupee />,
              color: "#36656B",
              bgColor: "rgba(54,101,107,0.12)",
            },
            {
              title: "Active Users",
              value: "0",
              change: "+8.1%",
              icon: <Visibility />,
              color: "#36656B",
              bgColor: "rgba(54,101,107,0.12)",
            },
          ],
          recentJobs: [],
          recentApplications: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ stat }) => {
    return (
      <Box
        className="stat-card"
        sx={{
          minHeight: { xs: 96, sm: 102, md: 108 },
          p: { xs: "10px 12px", sm: "12px 14px", md: "12px 16px" },
          gap: { xs: "10px", md: "12px" },
          borderRadius: { xs: "14px", md: "18px" },
        }}
      >
        <Box
          className="stat-icon"
          sx={{
            width: { xs: 34, sm: 36, md: 40 },
            height: { xs: 34, sm: 36, md: 40 },
            borderRadius: { xs: "9px", md: "10px" },
          }}
        >
          {React.cloneElement(stat.icon, {
            sx: { fontSize: { xs: "18px", sm: "20px", md: "22px" } },
          })}
        </Box>
        <Box className="stat-text">
          <Typography
            className="card-value"
            sx={{
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.65rem", md: "1.9rem" },
              lineHeight: 1,
              color: "#1E2A44",
            }}
          >
            {stat.value}
          </Typography>
          <Typography
            className="card-title"
            sx={{
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              color: "#4A5C78",
              fontSize: { xs: "12px", sm: "13px", md: "14px" },
              lineHeight: 1.25,
            }}
          >
            {stat.title}
          </Typography>
        </Box>
      </Box>
    );
  };

  const JobCard = ({ job }) => {
    const statusClass =
      job.status === "Active" ? "status-active" : job.status === "Draft" ? "status-draft" : "status-closed";

    return (
      <Box className="recent-job-card">
        <Box className="job-card-header">
          <Typography className="job-title">{job.title}</Typography>
          <span className={`status-badge ${statusClass}`}>{job.status}</span>
        </Box>

        <Typography className="job-description">{job.Description}</Typography>

        <Box className="job-footer">
          <Box>
            <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700, display: "block", textTransform: "uppercase", fontSize: "10px" }}>
              Budget
            </Typography>
            <Typography className="salary">{job.budget}</Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700, display: "block", textTransform: "uppercase", fontSize: "10px" }}>
              Applications
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: "14px", fontFamily: "'Montserrat', sans-serif" }}>
              {job.applications}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
            {job.projectDuration}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
            {job.postedDate}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6">Loading dashboard data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="dashboard-container">
      <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
        <Box className="stats-grid" sx={{ mt: 0 }}>
          {dashboardData.stats.map((stat, index) => (
            <StatCard key={index} stat={stat} />
          ))}
        </Box>

        <Box
          className="dashboard-split-grid"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.55fr 1fr" },
            gap: { xs: "16px", md: "24px" },
          }}
        >
          <Box
            className="section-card panel-jobs"
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 0 },
                mb: 2.5,
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: "18px", color: "#0F172A" }}>
                Recent Jobs
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/create-job")}
                startIcon={<Visibility />}
                sx={{
                  backgroundColor: "#36656B",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: "'Montserrat', sans-serif",
                  width: { xs: "100%", sm: "auto" },
                  alignSelf: { xs: "stretch", sm: "auto" },
                  "&:hover": { backgroundColor: "#2A4F54" },
                }}
              >
                View All
              </Button>
            </Box>

            <Box className="panel-scroll jobs-scroll" sx={{ overflowY: "auto" }}>
              <Box className="jobs-grid">
                {dashboardData.recentJobs.length > 0 ? (
                  dashboardData.recentJobs.map((job) => <JobCard key={job.id} job={job} />)
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4, gridColumn: "1/-1" }}>
                    No recent jobs found.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            className="section-card panel-applications"
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: "18px", color: "#0F172A", mb: 2.5 }}>
              Recent Applications
            </Typography>

            <Box className="panel-scroll applications-scroll" sx={{ overflowY: "auto" }}>
              {dashboardData.recentApplications && dashboardData.recentApplications.length > 0 ? (
                <List sx={{ display: "flex", flexDirection: "column", gap: "14px", p: "2px 0 0" }}>
                  {dashboardData.recentApplications.map((application) => (
                    <ListItem
                      key={application.id}
                      sx={{
                        p: "14px",
                        borderRadius: "14px",
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E6EAF0",
                        boxSizing: "border-box",
                        position: "relative",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        transition: "all 0.25s ease",
                        alignItems: "flex-start",
                        "&:hover": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#36656B",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                        },
                        "&:focus-visible, &:active": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#36656B",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ mr: 1, mt: 0.5 }}>
                        <Avatar
                          src={application.avatar}
                          alt={application.name}
                          sx={{
                            width: 42,
                            height: 42,
                            border: "2px solid #FFFFFF",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                              {application.name}
                            </Typography>
                            <Box className="badge-modern badge-warning" sx={{ fontSize: "10px", py: 0.2, px: 1 }}>
                              * {application.rating}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: "12px", mb: 0.8, color: "#64748B", fontWeight: 500 }}>
                              {application.profession}
                            </Typography>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box className="badge-modern badge-info" sx={{ fontSize: "9px" }}>
                                {application.domain}
                              </Box>
                              <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
                                {application.appliedDate}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        disableTypography
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "48px 20px",
                    minHeight: "260px",
                  }}
                >
                  <Box component="img" src={emptyApplicationsIllustration} sx={{ width: "140px", opacity: 0.8, mb: 2 }} />
                  <Typography className="card-title-modern" sx={{ fontSize: "15px", color: "#94A3B8" }}>
                    No applications yet
                  </Typography>
                  <Typography className="card-subtext">Candidates will appear here once they apply.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
